import { Mail } from 'lucide-react';

import { Flex } from 'components/layout';
import { Typography } from 'components/typography';

import { EmailRecipient } from './types';

interface EmailDropdownOptionProps {
  option: EmailRecipient;
  isHighlighted?: boolean;
  onSelect: (recipient: EmailRecipient) => void;
}

export default function EmailDropdownOption({
  option,
  isHighlighted = false,
  onSelect,
}: EmailDropdownOptionProps) {
  const handleClick = () => {
    onSelect(option);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(option);
    }
  };

  return (
    <div
      role='button'
      tabIndex={0}
      aria-label={`Select ${option.name || option.email}`}
      className={`p-3 cursor-pointer border-b border-neutral-200 last:border-b-0 ${
        isHighlighted
          ? 'bg-brand-50 border-brand-main hover:bg-brand-100'
          : 'hover:bg-neutral-100'
      } focus:outline-none focus:ring-1 focus:ring-brand-main`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <Flex direction='col' gap='xs' className='w-full'>
        <Flex align='center' gap='sm'>
          <Mail size={14} className='text-neutral-500 shrink-0 -ml-1' />
          <div className='min-w-0 flex-1'>
            {option.name && (
              <Typography
                variant='body-xs'
                weight='semibold'
                className='truncate -mb-0.5'
              >
                {option.name}
              </Typography>
            )}
            <Typography
              variant='body-xs'
              className={
                option.name
                  ? 'text-neutral-600'
                  : 'text-neutral-800 font-medium'
              }
            >
              {option.email}
            </Typography>
          </div>
        </Flex>

        {option.metadata && Object.keys(option.metadata).length > 0 && (
          <Flex direction='col' gap='xs' className='ml-5'>
            {Object.entries(option.metadata).map(([key, value]) => (
              <Flex
                key={key}
                justify='between'
                align='center'
                gap='sm'
                className='text-xs'
              >
                <Typography
                  variant='body-xs'
                  className='text-neutral-400 capitalize'
                >
                  {key}:
                </Typography>
                <Typography
                  variant='body-xs'
                  className='text-neutral-600 truncate'
                >
                  {value}
                </Typography>
              </Flex>
            ))}
          </Flex>
        )}
      </Flex>
    </div>
  );
}
